import React, { memo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  TableContainer,
  IconButton,
  Icon,
  Stack,
  Tooltip,
  Alert,
} from "investira.react.components";
import { validators, formats } from "investira.sdk";
import { Link } from "react-router-dom";
import { ATIVO_TIPO } from "@investirapri/financa.js/lib/enums";

import { useConsultaContext } from "../../../../contexts/ConsultaContext";
import { PessoaImage, NumberTableCell } from "../../../../components/molecules";
import { displays } from "investira.react.lib";
import { PATHS } from "../../../../enums";

const DetalheTable = memo((props) => {
  const { detalhe } = props;

  const { state } = useConsultaContext();

  function agruparPorAtivoTipoId(pDetalhe) {
    // Verifica se o input é um array
    if (!Array.isArray(pDetalhe)) {
      console.error("O parâmetro deve ser um array.");
      return {};
    }

    const agrupado = {};

    pDetalhe.forEach((item) => {
      const tipoId = item.ativo_tipo_id;

      // Se o tipo não existir no objeto, cria um array vazio para ele
      if (!agrupado[tipoId]) {
        agrupado[tipoId] = [];
      }

      // Adiciona o item ao grupo correspondente
      agrupado[tipoId].push(item);
    });

    return agrupado;
  }

  function calcRangeEnquadramento(pMin = 0, pMax = 50) {
    return Math.abs(pMax - pMin);
  }

  function calcAlvoRange(
    pAlvo = null,
    pAlvoLimites = {},
    pDesenquadradoAlvo = null,
    pDesenquadrado = null
  ) {
    let targetColors = "white";

    if (!validators.isNull(pDesenquadrado)) {
      targetColors = "error.light";
    }

    if (!validators.isNull(pDesenquadradoAlvo)) {
      targetColors = "warning.light";
    }

    return {
      alvo: pAlvo,
      alvoLimites: pAlvoLimites,
      alvoWidth: pAlvoLimites?.maximo - pAlvoLimites.minimo,
      desenquadradoAlvo: !validators.isNull(pDesenquadradoAlvo),
      pDesenquadrado: !validators.isNull(pDesenquadrado),
      noAlvo: !validators.isNull(pDesenquadradoAlvo),
      targetColor: targetColors,
    };
  }

  function calcObservado(pObservado) {
    if (pObservado <= 0) {
      return "0%";
    }

    return `calc(${pObservado}% - 1px)`;
  }

  function getDetalheFiltrado(detalhe, state) {
    return state.exibir_desenquadrado
      ? detalhe.filter((x) => x.desenquadrado)
      : detalhe;
  }

  return (
    <TableContainer
      id="table-container"
      sx={{
        overflow: "auto",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell colSpan={4}></TableCell>
            <TableCell colSpan={4} sx={{ textAlign: "center" }}>
              Volatilidade
            </TableCell>
            <TableCell colSpan={4} sx={{ textAlign: "center" }}>
              Valor-em-Risco
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              Ativo
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              ISIN
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              Financeiro
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              Participação %
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              Beta IBOV
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                width: "117px",
              }}
            >
              Sharpe
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                width: "117px",
              }}
            >
              Volatilidade
            </TableCell>

            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              Vol. EWMA
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              VaR (Paramétrico)
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              VaR (%)
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              Var (Histórico)
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", textAlign: "right" }}>
              VaR (%)
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {detalhe.map((xItem, xIndex) => {
            const bgColor =
              xIndex % 2 === 0 ? "secondary.darkness" : "secondary.dark";

            return (
              <React.Fragment key={crypto.randomUUID()}>
                {!validators.isEmpty(xItem.erros) && (
                  <TableRow sx={{ backgroundColor: bgColor }}>
                    <TableCell colSpan={12} padding="none">
                      <Stack spacing={0.25}>
                        {xItem.erros.map((xErr, xErrIndex) => {
                          return (
                            <Alert
                              key={`${xItem.isin}-erro-${xErrIndex}`}
                              severity="warning"
                              sx={{ py: 0.25, borderRadius: 0 }}
                              variant="outlined"
                            >
                              {xErr.descricao}
                            </Alert>
                          );
                        })}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ backgroundColor: bgColor }}>
                  <TableCell colSpan={5}>
                    <b>{xItem.ativo}</b>
                  </TableCell>
                  <TableCell colSpan={1}>
                    <b>{xItem.isin || "---"}</b>
                  </TableCell>
                  <TableCell colSpan={1} sx={{ textAlign: "right" }}>
                    <b>{xItem.ativo_tipo || "---"}</b>
                  </TableCell>
                  <TableCell colSpan={2} sx={{ textAlign: "right" }}>
                    {xItem.vencimento ? (
                      <b>Vencimento: {formats.formatDate(xItem.vencimento)}</b>
                    ) : (
                      <b>---</b>
                    )}
                  </TableCell>

                  <TableCell colSpan={5}></TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: bgColor }}>
                  <NumberTableCell value={xItem.curva?.pu_vna} />
                  <NumberTableCell value={xItem.curva?.pu_par} />
                  <NumberTableCell
                    value={xItem.pu_emissao}
                    decimal={4}
                    separateThousand={false}
                  />
                  <NumberTableCell
                    value={xItem.operacao?.taxa}
                    decimal={4}
                    separateThousand={false}
                  />
                  <NumberTableCell value={xItem.operacao?.pu} decimal={6} />
                  <NumberTableCell
                    value={xItem.operacao?.quantidade}
                    decimal={6}
                  />
                  <NumberTableCell
                    value={xItem.operacao?.financeiro}
                    decimal={2}
                  />
                  <NumberTableCell
                    value={xItem.operacao?.cotacao}
                    decimal={6}
                    separateThousand={false}
                  />
                  <NumberTableCell
                    value={xItem.analise?.prazo_medio}
                    decimal={6}
                    separateThousand={false}
                  />
                  <NumberTableCell
                    value={xItem.analise?.duration}
                    decimal={6}
                    separateThousand={false}
                  />
                  <NumberTableCell
                    value={xItem.analise?.duration_modificada}
                    decimal={6}
                    separateThousand={false}
                  />
                  <NumberTableCell
                    value={xItem.analise?.convexidade}
                    decimal={6}
                    separateThousand={false}
                  />
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

export default DetalheTable;
